import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import React from 'react';
import TransformSection from './common/transform-section';
import { registerInspector } from './inspector-registry';
import ColorField from './shared/color-field';
import FontLoadWarning from 'shared/components/font-load-warning';
import InspectorRow from './shared/inspector-row';
import InspectorSection from './shared/inspector-section';
import SlotFilterSection from './shared/slot-filter-section';
import VisibleWhenSection from './shared/visible-when-section';
import MixedSelectField from './shared/mixed-select-field';
import NumberField from './shared/number-field';
import TextField from './shared/text-field';
import { useCommonFabricProp } from './shared/use-common-fabric-prop';
import { useFontFields } from './shared/use-font-fields';
import type { ObjectInspectorProps } from './types';

const TEXT_ALIGNS = [
  { value: 'left', label: msg`Влево` },
  { value: 'center', label: msg`По центру` },
  { value: 'right', label: msg`Вправо` },
  { value: 'justify', label: msg`По ширине` },
];

const TextboxInspector: React.FC<ObjectInspectorProps> = ({ objects, editor }) => {
  const { t } = useLingui();
  const MIXED = t`(разное)`;
  const text = useCommonFabricProp<string>(objects, 'text', editor);
  const fontSize = useCommonFabricProp<number>(objects, 'fontSize', editor);
  const fontFamily = useCommonFabricProp<string>(objects, 'fontFamily', editor);
  const fontWeight = useCommonFabricProp<string>(objects, 'fontWeight', editor);
  const textAlign = useCommonFabricProp<string>(objects, 'textAlign', editor);
  const lineHeight = useCommonFabricProp<number>(objects, 'lineHeight', editor);
  const fill = useCommonFabricProp<string>(objects, 'fill', editor);
  const opacity = useCommonFabricProp<number>(objects, 'opacity', editor);

  const fonts = useFontFields(fontFamily, fontWeight);
  const textAligns = TEXT_ALIGNS.map((option) => ({ value: option.value, label: t(option.label) }));

  return (
    <>
      <TransformSection objects={objects} editor={editor} />
      <InspectorSection title={t`Содержимое`}>
        <InspectorRow label={t`Текст`}>
          <TextField
            value={text.isMixed ? '' : text.value}
            onChange={text.setValue}
            placeholder={text.isMixed ? MIXED : undefined}
            multiline
          />
        </InspectorRow>
      </InspectorSection>
      <InspectorSection title={t`Типографика`}>
        <InspectorRow label={t`Шрифт`}>
          <div className="flex items-center gap-1.5">
            <div className="min-w-0 flex-1">
              <MixedSelectField
                value={fontFamily.value}
                isMixed={fontFamily.isMixed}
                onChange={fonts.changeFamily}
                options={fonts.familyOptions}
              />
            </div>
            {!fontFamily.isMixed && <FontLoadWarning fontFamily={fontFamily.value} fontWeight={fontWeight.value} />}
          </div>
        </InspectorRow>
        <InspectorRow label={t`Размер`}>
          <NumberField
            value={fontSize.isMixed ? undefined : fontSize.value}
            onChange={fontSize.setValue}
            min={1}
            placeholder={fontSize.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
        <InspectorRow label={t`Начертание`}>
          <MixedSelectField
            value={fonts.weightValue}
            isMixed={fontWeight.isMixed}
            onChange={fonts.changeWeight}
            options={fonts.weightOptions}
          />
        </InspectorRow>
        <InspectorRow label={t`Выравнивание`}>
          <MixedSelectField value={textAlign.value} isMixed={textAlign.isMixed} onChange={textAlign.setValue} options={textAligns} />
        </InspectorRow>
        <InspectorRow label={t`Высота строки`}>
          <NumberField
            value={lineHeight.isMixed ? undefined : lineHeight.value}
            onChange={lineHeight.setValue}
            step={0.1}
            min={0.1}
            placeholder={lineHeight.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
      </InspectorSection>
      <InspectorSection title={t`Внешний вид`}>
        <InspectorRow label={t`Цвет`}>
          <ColorField
            value={fill.isMixed ? undefined : fill.value}
            onChange={fill.setValue}
            placeholder={fill.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
        <InspectorRow label={t`Прозрачность`}>
          <NumberField
            value={opacity.isMixed ? undefined : opacity.value}
            onChange={opacity.setValue}
            step={0.05}
            min={0}
            max={1}
            placeholder={opacity.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
      </InspectorSection>
      <SlotFilterSection objects={objects} editor={editor} />
      <VisibleWhenSection objects={objects} editor={editor} />
    </>
  );
};

export default TextboxInspector;

registerInspector('textbox', TextboxInspector);
registerInspector('i-text', TextboxInspector);
registerInspector('text', TextboxInspector);
